import { renderCards, CardDescriptor } from './cardRenderer';

interface SortingDetail {
    sortingPlanNo: string;
    bizSortingDetailNo: string;
    sortingDetailNo: string;
    palletNo: string;
    customerId: string;
    status: string;
    scanTime: number;
    markPrefix: string;
    markSerialNo: string;
    length: number;
    width: number;
    height: number;
    lengthUnit: string;
    volume: number;
    volumeUnit: string;
    weight: number;
    weightUnit: string;
}

interface ApiResponse<T> {
    success: boolean;
    code: number;
    message: string;
    data: T;
}

const API_BASE_URL = '/api/v1';
const COOKIE_STORAGE_KEY = 'sorting_plan_cookie';

let currentBusinessNo: string = '';
let currentDetails: SortingDetail[] = [];
// 选中顺序：sortingDetailNo → 选中序号（1-based）
let selectedOrder: Map<string, number> = new Map();
let selectionCounter: number = 0;

export function getCurrentDetails(): SortingDetail[] {
    return currentDetails;
}

export function getSelectedOrder(): Map<string, number> {
    return selectedOrder;
}

export function getSelectedDetails(): SortingDetail[] {
    const result: SortingDetail[] = [];
    // 按选中序号升序排列
    const sorted = [...selectedOrder.entries()].sort((a, b) => a[1] - b[1]);
    for (const [detailNo] of sorted) {
        const detail = currentDetails.find(d => d.sortingDetailNo === detailNo);
        if (detail) result.push(detail);
    }
    return result;
}

export function clearSelection() {
    selectedOrder.clear();
    selectionCounter = 0;
    updateExecuteButton();
    updateSelectAllButton();
    refreshSelectionUI();
}

function updateSelectAllButton() {
    const btn = document.getElementById('selectAllBtn');
    if (btn) btn.textContent = '全选';
}

function updateExecuteButton() {
    const btn = document.getElementById('executeSortingBtn') as HTMLButtonElement | null;
    if (btn) {
        btn.disabled = selectedOrder.size === 0;
    }
}

function refreshSelectionUI() {
    const container = document.getElementById('sortingDetailsContainer');
    if (!container) return;
    // 更新所有 detail-card 的选中状态和序号角标
    const cards = container.querySelectorAll<HTMLElement>('.detail-card');
    cards.forEach(card => {
        const detailNo = card.dataset.detailNo || '';
        const order = selectedOrder.get(detailNo);
        if (order !== undefined) {
            card.classList.add('selected');
            // 序号角标
            let badge = card.querySelector<HTMLElement>('.selection-order');
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'selection-order';
                card.style.position = 'relative';
                card.appendChild(badge);
            }
            badge.textContent = String(order);
        } else {
            card.classList.remove('selected');
            const badge = card.querySelector<HTMLElement>('.selection-order');
            if (badge) badge.remove();
        }
    });
}

function toggleCardSelection(detailNo: string) {
    if (selectedOrder.has(detailNo)) {
        // 取消选中：移除该项，后续序号保持不变（不重排）
        selectedOrder.delete(detailNo);
    } else {
        // 选中：分配新序号
        selectionCounter++;
        selectedOrder.set(detailNo, selectionCounter);
    }
    updateExecuteButton();
    updateSelectAllState();
    refreshSelectionUI();
}

function updateSelectAllState() {
    const btn = document.getElementById('selectAllBtn');
    if (!btn) return;
    btn.textContent = (selectedOrder.size === currentDetails.length && currentDetails.length > 0)
        ? '取消全选'
        : '全选';
}

export function initDailySorting() {
    bindTabSwitchEvents();
    bindCookieSettingsEvents();
    bindQueryEvents();
    bindExecuteButtonEvent();
    bindSelectAllButton();
    loadSavedCookie();
}

function bindTabSwitchEvents() {
    const tabBtns = document.querySelectorAll('.tab-btn');
    tabBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const tab = btn.getAttribute('data-tab');
            switchTab(tab);
        });
    });
}

function switchTab(tab: string | null) {
    if (!tab) return;

    // 切换顶部 tab 按钮高亮
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-tab') === tab);
    });

    // 按 data-page 匹配切换对应页面（algorithm/daily/check/load）
    document.querySelectorAll('.page-content').forEach(page => {
        page.classList.toggle('active', page.getAttribute('data-page') === tab);
    });
}

function bindCookieSettingsEvents() {
    const saveBtn = document.getElementById('saveCookieSettings');

    saveBtn?.addEventListener('click', () => {
        saveCookie();
    });
}

function loadSavedCookie() {
    const savedCookie = localStorage.getItem(COOKIE_STORAGE_KEY);
    const cookieInput = document.getElementById('cookieInput') as HTMLInputElement;
    if (savedCookie && cookieInput) {
        cookieInput.value = savedCookie;
    }
}

function saveCookie() {
    const cookieInput = document.getElementById('cookieInput') as HTMLInputElement;
    if (cookieInput) {
        const cookieValue = cookieInput.value.trim();
        if (cookieValue) {
            localStorage.setItem(COOKIE_STORAGE_KEY, cookieValue);
            alert('Cookie保存成功！');
        } else {
            alert('请输入Cookie字符串');
        }
    }
}

function bindQueryEvents() {
    const queryBtn = document.getElementById('querySortingPlan');
    queryBtn?.addEventListener('click', () => {
        querySortingDetailsByBusinessNo();
    });

    const businessNoInput = document.getElementById('businessNo') as HTMLInputElement;
    businessNoInput?.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            querySortingDetailsByBusinessNo();
        }
    });
}

function bindExecuteButtonEvent() {
    const btn = document.getElementById('executeSortingBtn');
    btn?.addEventListener('click', () => {
        // 动态导入避免循环依赖
        import('./executeSorting').then(m => m.openExecuteModal());
    });
}

function bindSelectAllButton() {
    const btn = document.getElementById('selectAllBtn');
    if (!btn) return;
    btn.addEventListener('click', () => {
        if (selectedOrder.size === currentDetails.length && currentDetails.length > 0) {
            // 已全选 → 取消全选
            clearSelection();
            btn.textContent = '全选';
        } else {
            // 全选所有卡片
            selectedOrder.clear();
            selectionCounter = 0;
            currentDetails.forEach(d => {
                selectionCounter++;
                selectedOrder.set(d.sortingDetailNo, selectionCounter);
            });
            updateExecuteButton();
            refreshSelectionUI();
            btn.textContent = '取消全选';
        }
    });
}

async function querySortingDetailsByBusinessNo() {
    const businessNoInput = document.getElementById('businessNo') as HTMLInputElement;
    const businessNo = businessNoInput?.value.trim();

    if (!businessNo) {
        alert('请输入业务编号');
        return;
    }

    currentBusinessNo = businessNo;
    await querySortingDetails(businessNo);
}

async function querySortingDetails(businessNo: string) {
    const cookie = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!cookie) {
        alert('请先设置Cookie');
        return;
    }

    // 只提取必要的认证字段 SID 和 TK
    const extractAuthCookies = (fullCookie: string): string => {
        const cookies = fullCookie.split(';').map(c => c.trim());
        const sid = cookies.find(c => c.startsWith('SID='));
        const tk = cookies.find(c => c.startsWith('TK='));
        const needed = [sid, tk].filter(Boolean).join('; ');
        console.log('提取的认证Cookie:', needed);
        return needed;
    };

    const authCookie = extractAuthCookies(cookie);

    showLoading('正在查询分拣明细...');

    const url = `${API_BASE_URL}/wfp/sortingPlan/detail/test?businessNo=${businessNo}`;

    console.log('=== 请求调试信息 ===');
    console.log('请求URL:', url);
    console.log('认证Cookie长度:', authCookie.length);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'X-Cookie': authCookie
            }
        });

        console.log('响应状态:', response.status, response.statusText);

        const responseText = await response.text();
        console.log('响应内容:', responseText);

        if (!response.ok) {
            console.error('请求失败:', response.status, responseText);
            throw new Error(`HTTP error! status: ${response.status}, body: ${responseText}`);
        }

        const result: ApiResponse<SortingDetail[]> = JSON.parse(responseText);

        if (result.success && result.data) {
            currentDetails = result.data;
            clearSelection();
            renderSortingDetails(currentDetails);
        } else {
            showEmpty('未找到分拣明细');
        }
    } catch (error) {
        console.error('查询分拣明细失败:', error);
        showError('查询分拣明细失败，请检查网络连接和Cookie设置');
    }
}

function renderSortingDetails(details: SortingDetail[]) {
    if (details.length === 0) {
        renderCards('sortingDetailsContainer', [], '', undefined, '暂无分拣明细数据');
        return;
    }

    const sortedDetails = [...details].sort((a, b) => {
        const prefixCompare = a.markPrefix.localeCompare(b.markPrefix);
        if (prefixCompare !== 0) return prefixCompare;
        return a.markSerialNo.localeCompare(b.markSerialNo);
    });

    const cards: CardDescriptor[] = sortedDetails.map(detail => {
        const mark = `${detail.markPrefix}/${detail.markSerialNo}`;
        const isCompleted = detail.status === 'COMPLETED';
        return {
            id: detail.sortingDetailNo,
            title: mark,
            info: `${detail.weight} ${detail.weightUnit}<br>${detail.volume} ${detail.volumeUnit}`,
            barcodeValue: mark,
            qrcodeValue: JSON.stringify({ id: mark, operation: 'fbm', type: 'inbound', source: 'seller' }),
            badges: [{ text: isCompleted ? '已完成' : '待处理', className: isCompleted ? 'completed' : 'pending' }]
        };
    });

    renderCards(
        'sortingDetailsContainer',
        cards,
        `共 <span class="details-count">${details.length}</span> 条记录`,
        () => { if (currentBusinessNo) querySortingDetails(currentBusinessNo); }
    );

    // 卡片渲染完成后绑定选中事件
    bindCardSelectionEvents(details);
}

let cardSelectionBound = false;

function bindCardSelectionEvents(details: SortingDetail[]) {
    const container = document.getElementById('sortingDetailsContainer');
    if (!container) return;

    // 给每张卡片添加 selectable 样式 + data-detail-no
    const cards = container.querySelectorAll<HTMLElement>('.detail-card');
    cards.forEach((card, index) => {
        card.classList.add('selectable');
        const detail = details[index];
        if (detail) {
            card.dataset.detailNo = detail.sortingDetailNo;
        }
    });

    // 用事件委托，只绑定一次
    if (cardSelectionBound) return;
    cardSelectionBound = true;

    container.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const card = target.closest<HTMLElement>('.detail-card');
        if (!card) return;
        // 排除交互元素
        if (target.closest('.barcode-copy-btn') ||
            target.closest('.code-tab') ||
            target.closest('canvas')) {
            return;
        }
        const detailNo = card.dataset.detailNo;
        if (detailNo) {
            toggleCardSelection(detailNo);
        }
    });
}

function showLoading(message: string) {
    const container = document.getElementById('sortingDetailsContainer');
    if (!container) return;

    container.innerHTML = `
        <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>${message}</p>
        </div>
    `;
}

function showEmpty(message: string) {
    const container = document.getElementById('sortingDetailsContainer');
    if (!container) return;

    container.innerHTML = `
        <div class="empty-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M2 7H18" stroke="currentColor" stroke-width="2"/>
                <path d="M7 2V7" stroke="currentColor" stroke-width="2"/>
                <path d="M13 2V7" stroke="currentColor" stroke-width="2"/>
            </svg>
            <p>${message}</p>
        </div>
    `;
}

function showError(message: string) {
    const container = document.getElementById('sortingDetailsContainer');
    if (!container) return;

    container.innerHTML = `
        <div class="error-state">
            <svg width="48" height="48" viewBox="0 0 20 20" fill="none" opacity="0.3">
                <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
                <path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <p class="error-message">${message}</p>
        </div>
    `;
}
